<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <button
          @click="$router.back()"
          class="flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-4 transition-colors"
        >
          <ChevronLeftIcon class="w-4 h-4 mr-1" />
          Back to Settings
        </button>
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <div class="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <CreditCardIcon class="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
                {{ organization?.name ? `${organization.name} Credits` : 'Credits & Billing' }}
              </h1>
              <p class="text-gray-600 dark:text-gray-400">
                Manage your organization's neural credits and view transaction history.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Left Column: Balance and Top-up -->
        <div class="lg:col-span-1 space-y-8">
          <!-- Balance Card -->
          <Card padding="none">
            <div class="p-6">
              <h3
                class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4"
              >
                Available Balance
              </h3>
              <div v-if="loadingCredits" class="animate-pulse space-y-3">
                <div class="h-10 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              </div>
              <template v-else>
                <div class="flex items-baseline mb-2">
                  <span class="text-4xl font-bold text-gray-900 dark:text-white">
                    {{ formattedBalance }}
                  </span>
                  <span class="ml-2 text-lg text-gray-500 dark:text-gray-400">credits</span>
                </div>
                <div class="flex items-center text-sm text-gray-500 dark:text-gray-400">
                  <ClockIcon class="w-4 h-4 mr-1.5" />
                  <span>Expires on {{ formattedExpiry }}</span>
                </div>
              </template>
            </div>
            <div
              class="bg-gray-50 dark:bg-gray-800/50 p-6 border-t border-gray-100 dark:border-gray-700"
            >
              <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-4">Quick Top-up</h4>
              <div class="grid grid-cols-2 gap-3 mb-4">
                <button
                  v-for="amount in topUpAmounts"
                  :key="amount.value"
                  @click="selectedAmount = amount.value"
                  :class="[
                    'px-4 py-2 text-sm font-medium rounded-lg border transition-all',
                    selectedAmount === amount.value
                      ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-500',
                  ]"
                >
                  {{ amount.label }}
                </button>
              </div>
              <Button full-width :loading="isPurchasing" :disabled="!selectedAmount" @click="handlePurchase">
                <span v-if="!isPurchasing">
                  Buy
                  {{
                    selectedAmount
                      ? topUpAmounts.find(a => a.value === selectedAmount)?.credits.toLocaleString()
                      : ''
                  }}
                  Credits
                </span>
              </Button>

              <TopUpModal
                v-model:open="topUpOpen"
                :amount-cents="selectedAmountCents"
                :credits="selectedCredits"
                :provider="provider"
                :lang="lang"
                :dark="isDark"
                @success="onTopUpSuccess"
              />
            </div>
          </Card>

          <!-- Usage Stats -->
          <Card>
            <h3
              class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-6"
            >
              Cost This Month
            </h3>
            <div v-if="loadingUsage" class="space-y-6">
              <div v-for="i in 3" :key="i" class="animate-pulse space-y-2">
                <div class="flex justify-between">
                  <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                  <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/8"></div>
                </div>
                <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5"></div>
              </div>
            </div>
            <div v-else class="space-y-6">
              <div
                v-for="modelCost in costUsage?.models"
                :key="modelCost.model"
                class="space-y-2"
              >
                <div class="flex justify-between text-sm">
                  <span class="text-gray-700 dark:text-gray-300">{{ modelCost.model }}</span>
                  <span class="font-medium text-gray-900 dark:text-white">
                    {{ formatCurrency(modelCost.total_cost, costUsage?.currency) }}
                  </span>
                </div>
                <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div
                    class="bg-blue-600 h-1.5 rounded-full"
                    :style="{ width: `${modelCost.percentage}%` }"
                  ></div>
                </div>
              </div>
              <div
                v-if="!costUsage?.models?.length"
                class="text-center py-4 text-gray-500 dark:text-gray-400 text-sm"
              >
                No cost data available for this month.
              </div>
            </div>
          </Card>
        </div>

        <!-- Right Column: History -->
        <div class="lg:col-span-2 space-y-8">
          <Card padding="none">
            <template #header>
              <div class="px-6 py-5 flex items-center justify-between">
                <h3 class="text-lg font-semibold text-text">
                  Transaction History
                </h3>
                <div class="flex items-center space-x-2">
                  <button
                    @click="refreshHistory"
                    class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <ArrowPathIcon class="w-5 h-5" :class="{ 'animate-spin': loadingHistory }" />
                  </button>
                </div>
              </div>
            </template>

            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      Date
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      Description
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      Amount
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                  <template v-if="loadingHistory">
                    <tr v-for="i in 5" :key="i">
                      <td class="px-6 py-4 whitespace-nowrap">
                        <div
                          class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 animate-pulse"
                        ></div>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <div
                          class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 animate-pulse"
                        ></div>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <div
                          class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16 animate-pulse"
                        ></div>
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <div
                          class="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-20 animate-pulse"
                        ></div>
                      </td>
                    </tr>
                  </template>
                  <template v-else>
                    <tr
                      v-for="tx in transactions"
                      :key="tx.id"
                      class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td
                        class="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400"
                      >
                        {{ new Date(tx.created_at).toLocaleDateString() }}
                      </td>
                      <td
                        class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Credits Purchase
                      </td>
                      <td
                        class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium"
                      >
                        +{{ tx.credits_amount.toLocaleString() }}
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <span
                          :class="[
                            'px-2.5 py-0.5 rounded-full text-xs font-medium',
                            statusClasses[tx.status] || 'bg-gray-100 text-gray-800',
                          ]"
                        >
                          {{ tx.status.charAt(0).toUpperCase() + tx.status.slice(1) }}
                        </span>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="!loadingHistory && transactions.length === 0">
                    <td colspan="4" class="px-6 py-10 text-center text-gray-500 dark:text-gray-400">
                      No transactions found.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              v-if="totalPages > 1"
              class="px-6 py-4 border-t border-border bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between"
            >
              <Button variant="outline" size="sm" :disabled="currentPage === 1" @click="currentPage--">
                Previous
              </Button>
              <span class="text-sm text-gray-600 dark:text-gray-400">
                Page {{ currentPage }} of {{ totalPages }}
              </span>
              <Button variant="outline" size="sm" :disabled="currentPage === totalPages" @click="currentPage++">
                Next
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import {
    ChevronLeftIcon,
    CreditCardIcon,
    ClockIcon,
    ArrowPathIcon,
  } from '@heroicons/vue/24/outline';
  import { useNotification, Card, Button } from '@asabdev/asab-ui';
  import { creditsApi } from '@/lib/api/credits';
  import { analyticsApi } from '@/lib/api/analytics';
  import type { UserCredits, PaymentTransaction } from '@/types/credits';
  import { useOrganization } from '../composables';
  import TopUpModal from '@/features/billing/components/TopUpModal.vue';
  import type { BillingProvider } from '../types';
  import type { TopUpResponse } from '@/features/billing/types';

  const route = useRoute();
  const orgId = route.params.id as string;
  const { organization, initialize } = useOrganization(orgId);
  const { showError, showSuccess } = useNotification();

  const credits = ref<UserCredits | null>(null);
  const transactions = ref<PaymentTransaction[]>([]);
  interface CostUsageModel {
    model: string;
    total_cost: number;
    percentage: number;
  }

  interface CostUsageSummary {
    totalCost: number;
    currency: string;
    models: CostUsageModel[];
  }

  const costUsage = ref<CostUsageSummary | null>(null);

  const loadingCredits = ref(true);
  const loadingHistory = ref(true);
  const loadingUsage = ref(true);
  const isPurchasing = ref(false);

  const currentPage = ref(1);
  const totalPages = ref(1);
  const selectedAmount = ref<number | null>(10);

  const topUpAmounts = [
    { label: '$10', value: 10, credits: 10000 },
    { label: '$25', value: 25, credits: 25000 },
    { label: '$50', value: 50, credits: 50000 },
    { label: '$100', value: 100, credits: 100000 },
  ];

  const statusClasses: Record<string, string> = {
    completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };

  const formattedBalance = computed(() => {
    return new Intl.NumberFormat().format(credits.value?.balance ?? credits.value?.amount ?? 0);
  });

  const formattedExpiry = computed(() => {
    if (!credits.value?.expiry) return 'N/A';
    return new Date(credits.value.expiry).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  });

  const fetchCredits = async () => {
    if (!organization.value?.id) return;
    try {
      loadingCredits.value = true;
      credits.value = await creditsApi.getOrgCredits(organization.value.id);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
      showError('Failed to fetch credits. Please try again later.');
    } finally {
      loadingCredits.value = false;
    }
  };

  const fetchHistory = async () => {
    if (!organization.value?.id) return;
    try {
      loadingHistory.value = true;
      const data = await creditsApi.getPaymentHistory(organization.value.id, currentPage.value);
      transactions.value = data.transactions;
      totalPages.value = data.total_pages;
    } catch (error) {
      console.error('Failed to fetch transaction history:', error);
      showError('Failed to fetch transaction history.');
    } finally {
      loadingHistory.value = false;
    }
  };

  const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null;
  };

  const getMonthRange = (): { start: string; end: string } => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const parseCostsSummary = (data: unknown): { totalCost: number; currency: string } | null => {
    if (!isRecord(data)) return null;
    const totalCost = typeof data.total_cost === 'number' ? data.total_cost : null;
    if (totalCost === null) return null;
    const currency = typeof data.currency === 'string' ? data.currency : 'USD';
    return { totalCost, currency };
  };

  const parseCostsByModel = (data: unknown): Array<{ model: string; total_cost: number }> => {
    if (!isRecord(data)) return [];
    const models = data.models;
    if (!Array.isArray(models)) return [];
    return models
      .map(model => {
        if (!isRecord(model)) return null;
        const modelName = typeof model.model === 'string' ? model.model : null;
        const totalCost = typeof model.total_cost === 'number' ? model.total_cost : null;
        if (!modelName || totalCost === null) return null;
        return { model: modelName, total_cost: totalCost };
      })
      .filter((item): item is { model: string; total_cost: number } => item !== null);
  };

  const formatCurrency = (value: number, currency: string = 'USD'): string => {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const fetchUsage = async () => {
    try {
      loadingUsage.value = true;
      const { start, end } = getMonthRange();
      const [summaryResponse, byModelResponse] = await Promise.all([
        analyticsApi.getCostsSummary({ start, end, granularity: 'daily' }),
        analyticsApi.getCostsByModel({ start, end }),
      ]);

      const summary = parseCostsSummary(summaryResponse);
      const models = parseCostsByModel(byModelResponse);
      if (!summary) {
        costUsage.value = { totalCost: 0, currency: 'USD', models: [] };
        return;
      }

      const totalCost = summary.totalCost;
      const normalizedModels = models.map(model => ({
        ...model,
        percentage: totalCost > 0 ? (model.total_cost / totalCost) * 100 : 0,
      }));

      costUsage.value = {
        totalCost,
        currency: summary.currency,
        models: normalizedModels,
      };
    } catch (error) {
      console.error('Failed to fetch cost usage:', error);
      showError('Failed to fetch cost usage statistics.');
      costUsage.value = null;
    } finally {
      loadingUsage.value = false;
    }
  };

  // New flow: open 2Pay.js modal routed through asab-accounts /v1/billing/topup.
  const topUpOpen = ref(false);
  const provider = ref<BillingProvider>(
    ((import.meta.env.VITE_BILLING_PROVIDER as string | undefined) ?? 'manual') as BillingProvider,
  );
  const lang = computed(() => document.documentElement.lang || 'en');
  const isDark = computed(() => document.documentElement.classList.contains('dark'));

  const selectedAmountCents = computed(() => {
    const m = topUpAmounts.find(a => a.value === selectedAmount.value);
    return m ? m.value * 100 : 0;
  });
  const selectedCredits = computed(() => {
    const m = topUpAmounts.find(a => a.value === selectedAmount.value);
    return m ? m.credits : 0;
  });

  const handlePurchase = () => {
    if (!selectedAmount.value || !organization.value?.id) return;
    topUpOpen.value = true;
  };

  const onTopUpSuccess = (resp: TopUpResponse) => {
    showSuccess(`Added ${resp.credits_granted.toLocaleString()} credits. New balance: ${resp.new_balance.toLocaleString()}`);
    fetchCredits();
    fetchHistory();
  };

  const refreshHistory = () => {
    currentPage.value = 1;
    fetchHistory();
  };

  watch(
    organization,
    newOrg => {
      if (newOrg?.id) {
        fetchCredits();
        fetchHistory();
        fetchUsage();
      }
    },
    { immediate: true }
  );

  watch(currentPage, () => {
    fetchHistory();
  });

  onMounted(async () => {
    await initialize();
    fetchCredits();
    fetchHistory();
    fetchUsage();
  });
</script>
